import {
  ArrowLeft,
  CheckCircle2,
  Download,
  Loader2,
  X,
  XCircle,
} from 'lucide-react';

import {
  useEffect,
  useState,
} from 'react';

import {
  useNavigate,
} from 'react-router-dom';

import {
  exportAndDownloadActivityLogRawData,
} from '../reports/services/publicReport.service';

import {
  getTodayDate,
} from '../reports/utils/reportDate.utils';


// ============================================================
// TOAST TYPES
// ============================================================

type ToastType =
  | 'success'
  | 'error';


interface ToastState {
  type: ToastType;
  message: string;
}


// ============================================================
// PAGE
// ============================================================

const ActivityLogReportPage =
  () => {

    const navigate =
      useNavigate();


    // ==========================================================
    // TODAY
    // ==========================================================

    const today =
      getTodayDate();


    // ==========================================================
    // FROM DATE
    // ==========================================================

    const [
      fromDate,
      setFromDate,
    ] = useState<string>(
      today,
    );


    // ==========================================================
    // TO DATE
    // ==========================================================

    const [
      toDate,
      setToDate,
    ] = useState<string>(
      today,
    );


    // ==========================================================
    // EMPLOYEE CODE
    // ==========================================================

    const [
      employeeCode,
      setEmployeeCode,
    ] = useState<string>(
      '',
    );


    // ==========================================================
    // PAGE NUMBER
    // ==========================================================

    const pageNumber =
      1;


    // ==========================================================
    // PAGE SIZE
    // ==========================================================

    const pageSize =
      10000;


    // ==========================================================
    // EXPORT STATE
    // ==========================================================

    const [
      isExporting,
      setIsExporting,
    ] = useState<boolean>(
      false,
    );


    // ==========================================================
    // TOAST
    // ==========================================================

    const [
      toast,
      setToast,
    ] = useState<ToastState | null>(
      null,
    );


    // ==========================================================
    // SHOW TOAST
    // ==========================================================

    const showToast = (
      type: ToastType,
      message: string,
    ): void => {

      setToast(
        {
          type,
          message,
        },
      );
    };


    // ==========================================================
    // AUTO CLOSE TOAST
    // ==========================================================

    useEffect(
      () => {

        if (!toast) {
          return;
        }


        const timer =
          window.setTimeout(
            () => {

              setToast(
                null,
              );

            },
            5000,
          );


        return () => {

          window.clearTimeout(
            timer,
          );
        };

      },
      [toast],
    );


    // ==========================================================
    // EXPORT
    // ==========================================================

    const handleExport =
      async (): Promise<void> => {

        // ======================================================
        // VALIDATE DATES
        // ======================================================

        if (
          !fromDate ||
          !toDate
        ) {

          showToast(
            'error',
            'Please select both From Date and To Date.',
          );

          return;
        }


        // ======================================================
        // VALIDATE DATE RANGE
        // ======================================================

        if (
          fromDate >
          toDate
        ) {

          showToast(
            'error',
            'From Date cannot be greater than To Date.',
          );

          return;
        }


        // ======================================================
        // START EXPORT
        // ======================================================

        try {

          setIsExporting(
            true,
          );


          // ====================================================
          // CLEAN EMPLOYEE CODE
          // ====================================================

          const cleanedEmployeeCode =
            employeeCode.trim() ||
            null;


          // ====================================================
          // LOG REQUEST
          // ====================================================

          console.log(
            '====================================================',
          );

          console.log(
            'ACTIVITY LOG EXPORT',
          );

          console.log(
            '====================================================',
          );

          console.log(
            {
              fromDate,
              toDate,
              employeeCode:
                cleanedEmployeeCode,
              pageNumber,
              pageSize,
            },
          );


          // ====================================================
          // CALL BACKEND
          //
          // Backend decides:
          //
          // 1 file
          //    -> text/csv
          //    -> browser downloads CSV
          //
          // Multiple files
          //    -> application/zip
          //    -> browser downloads ZIP
          // ====================================================

          const downloadedFileName =
            await exportAndDownloadActivityLogRawData(
              {
                fromDate,

                toDate,

                employeeCode:
                  cleanedEmployeeCode,

                pageNumber,

                pageSize,
              },
            );


          // ====================================================
          // SUCCESS
          // ====================================================

          showToast(
            'success',
            `${downloadedFileName} downloaded successfully.`,
          );

        } catch (error) {

          // ======================================================
          // LOG ERROR
          // ======================================================

          console.error(
            'Failed to export Activity Log report:',
            error,
          );


          // ======================================================
          // ERROR MESSAGE
          // ======================================================

          const message =
            error instanceof Error
              ? error.message
              : 'Failed to export Activity Log report. Please try again.';


          showToast(
            'error',
            message,
          );

        } finally {

          // ======================================================
          // STOP LOADING
          // ======================================================

          setIsExporting(
            false,
          );
        }
      };


    // ==========================================================
    // UI
    // ==========================================================

    return (
      <main
        className="
          min-h-screen
          bg-[#eef3f8]
          px-4
          py-5
          sm:px-6
          lg:px-8
        "
      >

        {/* ======================================================
            TOAST
        ====================================================== */}

        {toast && (

          <div
            className="
              fixed
              right-4
              top-5
              z-[9999]
              w-[calc(100%-2rem)]
              max-w-[500px]
            "
          >

            <div
              className={`
                flex
                items-start
                gap-3
                rounded-xl
                border
                bg-white
                px-4
                py-3
                shadow-xl

                ${
                  toast.type === 'success'
                    ? 'border-emerald-200'
                    : 'border-red-200'
                }
              `}
            >

              {/* ==================================================
                  ICON
              ================================================== */}

              {toast.type === 'success' ? (

                <CheckCircle2
                  size={21}
                  className="
                    mt-0.5
                    shrink-0
                    text-emerald-600
                  "
                />

              ) : (

                <XCircle
                  size={21}
                  className="
                    mt-0.5
                    shrink-0
                    text-red-600
                  "
                />
              )}


              {/* ==================================================
                  MESSAGE
              ================================================== */}

              <div
                className="
                  min-w-0
                  flex-1
                "
              >

                <p
                  className={`
                    break-words
                    text-sm
                    font-medium

                    ${
                      toast.type === 'success'
                        ? 'text-emerald-800'
                        : 'text-red-700'
                    }
                  `}
                >
                  {toast.message}
                </p>

              </div>


              {/* ==================================================
                  CLOSE
              ================================================== */}

              <button
                type="button"
                onClick={() =>
                  setToast(null)
                }
                className="
                  shrink-0
                  text-slate-400
                  hover:text-slate-700
                "
              >

                <X
                  size={17}
                />

              </button>

            </div>

          </div>
        )}


        {/* ======================================================
            CONTAINER
        ====================================================== */}

        <div
          className="
            mx-auto
            w-full
            max-w-[1440px]
          "
        >

          {/* ====================================================
              HEADER
          ==================================================== */}

          <section
            className="
              mb-5
              rounded-xl
              border
              border-slate-200
              bg-white
              shadow-sm
            "
          >

            <div
              className="
                flex
                items-center
                gap-3
                px-4
                py-4
                sm:px-5
              "
            >

              {/* ==================================================
                  BACK
              ================================================== */}

              <button
                type="button"
                onClick={() =>
                  navigate(
                    '/public-report',
                  )
                }
                className="
                  flex
                  h-10
                  w-10
                  items-center
                  justify-center
                  rounded-lg
                  border
                  border-slate-200
                  bg-white
                  text-slate-500
                  hover:bg-teal-50
                  hover:text-teal-600
                "
              >

                <ArrowLeft
                  size={18}
                />

              </button>


              {/* ==================================================
                  TITLE
              ================================================== */}

              <div>

                <h1
                  className="
                    text-lg
                    font-semibold
                    text-slate-900
                    sm:text-xl
                  "
                >
                  Activity Log Report
                </h1>


                <p
                  className="
                    mt-0.5
                    text-sm
                    text-slate-500
                  "
                >
                  Select the reporting period and optionally filter by Employee Code.
                </p>

              </div>

            </div>

          </section>


          {/* ====================================================
              FILTER
          ==================================================== */}

          <section
            className="
              rounded-xl
              border
              border-slate-200
              bg-white
              p-4
              shadow-sm
              sm:p-5
            "
          >

            <div
              className="
                grid
                grid-cols-1
                gap-4
                md:grid-cols-3
              "
            >

              {/* ==================================================
                  FROM DATE
              ================================================== */}

              <div>

                <label
                  htmlFor="activity-from-date"
                  className="
                    mb-1.5
                    block
                    text-xs
                    font-medium
                    text-slate-700
                  "
                >
                  From Date
                </label>


                <input
                  id="activity-from-date"
                  type="date"
                  value={fromDate}
                  max={toDate}
                  onChange={(event) =>
                    setFromDate(
                      event.target.value,
                    )
                  }
                  className="
                    h-10
                    w-full
                    rounded-lg
                    border
                    border-slate-200
                    bg-white
                    px-3
                    text-sm
                    text-slate-700
                    outline-none
                    focus:border-teal-500
                    focus:ring-2
                    focus:ring-teal-100
                  "
                />

              </div>


              {/* ==================================================
                  TO DATE
              ================================================== */}

              <div>

                <label
                  htmlFor="activity-to-date"
                  className="
                    mb-1.5
                    block
                    text-xs
                    font-medium
                    text-slate-700
                  "
                >
                  To Date
                </label>


                <input
                  id="activity-to-date"
                  type="date"
                  value={toDate}
                  min={fromDate}
                  onChange={(event) =>
                    setToDate(
                      event.target.value,
                    )
                  }
                  className="
                    h-10
                    w-full
                    rounded-lg
                    border
                    border-slate-200
                    bg-white
                    px-3
                    text-sm
                    text-slate-700
                    outline-none
                    focus:border-teal-500
                    focus:ring-2
                    focus:ring-teal-100
                  "
                />

              </div>


              {/* ==================================================
                  EMPLOYEE CODE
              ================================================== */}

              <div>

                <label
                  htmlFor="activity-employee-code"
                  className="
                    mb-1.5
                    block
                    text-xs
                    font-medium
                    text-slate-700
                  "
                >
                  Employee Code
                </label>


                <input
                  id="activity-employee-code"
                  type="text"
                  value={employeeCode}
                  placeholder="e.g. EMP001"
                  maxLength={100}
                  onChange={(event) =>
                    setEmployeeCode(
                      event.target.value,
                    )
                  }
                  className="
                    h-10
                    w-full
                    rounded-lg
                    border
                    border-slate-200
                    bg-white
                    px-3
                    text-sm
                    uppercase
                    text-slate-700
                    outline-none
                    placeholder:normal-case
                    placeholder:text-slate-400
                    focus:border-teal-500
                    focus:ring-2
                    focus:ring-teal-100
                  "
                />

              </div>

            </div>


            {/* ==================================================
                EXPORT BUTTON
            ================================================== */}

            <div
              className="
                mt-5
                flex
                justify-end
                border-t
                border-slate-100
                pt-4
              "
            >

              <button
                type="button"
                onClick={handleExport}
                disabled={isExporting}
                className="
                  inline-flex
                  h-10
                  items-center
                  justify-center
                  gap-2
                  rounded-lg
                  bg-teal-600
                  px-5
                  text-sm
                  font-medium
                  text-white
                  hover:bg-teal-700
                  disabled:cursor-not-allowed
                  disabled:opacity-60
                "
              >

                {isExporting ? (

                  <>
                    <Loader2
                      size={16}
                      className="
                        animate-spin
                      "
                    />

                    Downloading...
                  </>

                ) : (

                  <>
                    <Download
                      size={16}
                    />

                    Export CSV / ZIP
                  </>

                )}

              </button>

            </div>

          </section>

        </div>

      </main>
    );
  };


export default ActivityLogReportPage;